import { Fragment } from 'react';
import { Button, Table, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import Loading from '../../utils/Loding';
import LookupExpandedRow from './LookupExpandedRow';
import { LookupApp } from './types';
import {
  formatNumber,
  formatCurrency,
  formatPercentage,
  getCountryCodes,
  getMetricValue
} from './helpers';

type SortColumn = 'title' | 'countries' | 'clicks' | 'fillRate' | 'conversions' | 'conversionRate' | 'revenue';
type SortDirection = 'asc' | 'desc';
type SortTimeframe = 'today' | 'week';

interface LookupResultsTableProps {
  loading: boolean;
  sortedApps: LookupApp[];
  displayedApps: LookupApp[];
  displayLimit: number;
  hasMoreResults: boolean;
  isFetching: boolean;
  observerRef: React.RefObject<HTMLDivElement>;
  showStats: 'overall' | 'matched';
  setShowStats: (value: 'overall' | 'matched') => void;
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  sortTimeframe: SortTimeframe;
  onSort: (column: SortColumn, timeframe?: 'today' | 'week') => void;
  onShowStats: (app: LookupApp, clink?: any) => void;
  onCreateClink: (app: LookupApp, campaignId?: string, demandSourceId?: string) => void;
  onExpandApp: (app: LookupApp) => void;
  onSelectTab: (app: LookupApp, tabName: string) => void;
  onEditClink: (clink: any, app?: LookupApp) => void;
  onShowTrackingLinks: (clink: any, app?: LookupApp) => void;
  onShowHistory: (clink: any) => void;
}

const LookupResultsTable = ({
  loading,
  sortedApps,
  displayedApps,
  displayLimit,
  hasMoreResults,
  isFetching,
  observerRef,
  showStats,
  setShowStats,
  sortColumn,
  sortDirection,
  sortTimeframe,
  onSort,
  onShowStats,
  onCreateClink,
  onExpandApp,
  onSelectTab,
  onEditClink,
  onShowTrackingLinks,
  onShowHistory
}: LookupResultsTableProps) => {

  return (
    <>
      {/* Results Summary */}
      {!loading && sortedApps.length > 0 && (
        <div style={{fontWeight: 'bold', paddingTop: '10px', clear: 'both', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <div style={{display: 'inline'}}>
            Results - {sortedApps.length} Products - {sortedApps.reduce((total, app) => total + (app.counts?.appMatchedCampaigns || 0), 0)} matching campaigns - {sortedApps.reduce((total, app) => total + (app.counts?.appMatchedClinks || 0), 0)} matching cLinks
          </div>
          
          {/* Stats Toggle Buttons */}
          <div className="d-flex align-items-center">
            <span style={{ fontSize: '12px', marginRight: '8px', fontWeight: 'bold' }}>
              Show stats for:
            </span>
            <ToggleButtonGroup 
              type="radio" 
              name="showStats" 
              value={showStats} 
              onChange={(value: string) => setShowStats(value as 'overall' | 'matched')}
            >
              <ToggleButton 
                value="matched" 
                size="sm" 
                className={showStats === 'matched' ? 'active-toggle-button' : 'inactive-toggle-button'}
                style={{ fontSize: '11px' }}
                title="Show stats only for the campaigns that match your search criteria"
              >
                Matching Campaigns
              </ToggleButton>
              <ToggleButton 
                value="overall" 
                size="sm" 
                className={showStats === 'overall' ? 'active-toggle-button' : 'inactive-toggle-button'}
                style={{ fontSize: '11px' }}
                title="Shows stats for all campaigns, both active and inactive, including campaigns not matching your search criteria."
              >
                All Campaigns
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
        </div>
      )}

      {/* Results Table */}
      <div className="results-section mt-3" style={{width: '100%', overflowX: 'auto'}}>
        <Table striped bordered hover size="sm" style={{width: '100%', margin: 0, tableLayout: 'fixed'}} className="lookup-table">
          <thead>
            <tr>
              <th
                style={{
                  cursor: 'pointer', 
                  width: '20%',
                  fontWeight: sortColumn === 'title' ? 'bold' : 'normal'
                }}
                onClick={() => onSort('title')}
              >
                Product {sortColumn === 'title' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th
                style={{
                  cursor: 'pointer', 
                  width: '12%',
                  fontWeight: sortColumn === 'countries' ? 'bold' : 'normal'
                }}
                onClick={() => onSort('countries')}
              >
                Countries {sortColumn === 'countries' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th style={{width: '10%', textAlign: 'right'}}>
                <div>Clicks</div>
                <div style={{fontSize: '0.8em', color: '#ccc'}}>
                    <span
                        style={{
                            cursor: 'pointer',
                            textDecoration: 'none',
                            fontWeight: sortColumn === 'clicks' && sortTimeframe === 'today' ? 'bold' : 'normal',
                            color: sortColumn === 'clicks' && sortTimeframe === 'today' ? '#000' : '#ccc'
                        }}
                        onClick={() => onSort('clicks', 'today')}
                    >
                      1d {sortColumn === 'clicks' && sortTimeframe === 'today' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </span>
                  {' / '}
                  <span
                    style={{
                      cursor: 'pointer',
                      textDecoration: 'none',
                      fontWeight: sortColumn === 'clicks' && sortTimeframe === 'week' ? 'bold' : 'normal',
                      color: sortColumn === 'clicks' && sortTimeframe === 'week' ? '#000' : '#ccc'
                    }}
                    onClick={() => onSort('clicks', 'week')}
                  >
                    7d {sortColumn === 'clicks' && sortTimeframe === 'week' && (sortDirection === 'asc' ? '▲' : '▼')}
                  </span>
                </div>
              </th>
              <th style={{width: '10%', textAlign: 'right'}}>
                <div>Fill Rate</div>
                <div style={{fontSize: '0.8em', color: '#ccc'}}>
                    <span
                        style={{
                            cursor: 'pointer',
                            textDecoration: 'none',
                            fontWeight: sortColumn === 'fillRate' && sortTimeframe === 'today' ? 'bold' : 'normal',
                            color: sortColumn === 'fillRate' && sortTimeframe === 'today' ? '#000' : '#ccc'
                        }}
                        onClick={() => onSort('fillRate', 'today')}
                    >
                      1d {sortColumn === 'fillRate' && sortTimeframe === 'today' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </span>
                  {' / '}
                  <span
                    style={{
                      cursor: 'pointer',
                      textDecoration: 'none',
                      fontWeight: sortColumn === 'fillRate' && sortTimeframe === 'week' ? 'bold' : 'normal',
                      color: sortColumn === 'fillRate' && sortTimeframe === 'week' ? '#000' : '#ccc'
                    }}
                    onClick={() => onSort('fillRate', 'week')}
                  >
                    7d {sortColumn === 'fillRate' && sortTimeframe === 'week' && (sortDirection === 'asc' ? '▲' : '▼')}
                  </span>
                </div>
              </th>
              <th style={{width: '10%', textAlign: 'right'}}>
                <div>Conv.</div>
                <div style={{fontSize: '0.8em', color: '#ccc'}}>
                    <span
                        style={{
                            cursor: 'pointer',
                            textDecoration: 'none',
                            fontWeight: sortColumn === 'conversions' && sortTimeframe === 'today' ? 'bold' : 'normal',
                            color: sortColumn === 'conversions' && sortTimeframe === 'today' ? '#000' : '#ccc'
                        }}
                        onClick={() => onSort('conversions', 'today')}
                    >
                      1d {sortColumn === 'conversions' && sortTimeframe === 'today' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </span>
                  {' / '}
                  <span
                    style={{
                      cursor: 'pointer',
                      textDecoration: 'none',
                      fontWeight: sortColumn === 'conversions' && sortTimeframe === 'week' ? 'bold' : 'normal',
                      color: sortColumn === 'conversions' && sortTimeframe === 'week' ? '#000' : '#ccc'
                    }}
                    onClick={() => onSort('conversions', 'week')}
                  >
                    7d {sortColumn === 'conversions' && sortTimeframe === 'week' && (sortDirection === 'asc' ? '▲' : '▼')}
                  </span>
                </div>
              </th>
              <th style={{width: '10%', textAlign: 'right'}}>
                <div>Conv. Rate</div>
                <div style={{fontSize: '0.8em', color: '#ccc'}}>
                    <span
                        style={{
                            cursor: 'pointer',
                            textDecoration: 'none',
                            fontWeight: sortColumn === 'conversionRate' && sortTimeframe === 'today' ? 'bold' : 'normal',
                            color: sortColumn === 'conversionRate' && sortTimeframe === 'today' ? '#000' : '#ccc'
                        }}
                        onClick={() => onSort('conversionRate', 'today')}
                    >
                      1d {sortColumn === 'conversionRate' && sortTimeframe === 'today' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </span>
                  {' / '}
                  <span
                    style={{
                      cursor: 'pointer',
                      textDecoration: 'none',
                      fontWeight: sortColumn === 'conversionRate' && sortTimeframe === 'week' ? 'bold' : 'normal',
                      color: sortColumn === 'conversionRate' && sortTimeframe === 'week' ? '#000' : '#ccc'
                    }}
                    onClick={() => onSort('conversionRate', 'week')}
                  >
                    7d {sortColumn === 'conversionRate' && sortTimeframe === 'week' && (sortDirection === 'asc' ? '▲' : '▼')}
                  </span>
                </div>
              </th>
              <th style={{width: '12%', textAlign: 'right'}}>
                <div>Revenue</div>
                <div style={{fontSize: '0.8em', color: '#ccc'}}>
                    <span
                        style={{
                            cursor: 'pointer',
                            textDecoration: 'none',
                            fontWeight: sortColumn === 'revenue' && sortTimeframe === 'today' ? 'bold' : 'normal',
                            color: sortColumn === 'revenue' && sortTimeframe === 'today' ? '#000' : '#ccc'
                        }}
                        onClick={() => onSort('revenue', 'today')}
                    >
                      1d {sortColumn === 'revenue' && sortTimeframe === 'today' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </span>
                  {' / '}
                  <span
                    style={{
                      cursor: 'pointer',
                      textDecoration: 'none',
                      fontWeight: sortColumn === 'revenue' && sortTimeframe === 'week' ? 'bold' : 'normal',
                      color: sortColumn === 'revenue' && sortTimeframe === 'week' ? '#000' : '#ccc'
                    }}
                    onClick={() => onSort('revenue', 'week')}
                  >
                    7d {sortColumn === 'revenue' && sortTimeframe === 'week' && (sortDirection === 'asc' ? '▲' : '▼')}
                  </span>
                </div>
              </th>
              <th style={{width: '16%', textAlign: 'right'}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7}>
                  <Loading />
                </td>
              </tr>
            ) : displayedApps.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center">
                  No results found
                </td>
              </tr>
            ) : (
              displayedApps.map((app) => (
                <Fragment key={app.appID}>
                  <tr>
                    <td>
                      <div>
                        {app.storeLink ? (
                          <a href={app.storeLink} target="_blank" rel="noopener noreferrer" title={app.title}>
                            {app.title}
                          </a>
                        ) : (
                          <span title={app.title}>{app.title}</span>
                        )}
                        <br/>
                        <span style={{fontSize: '0.85em', color: '#666666'}}>{app.appID}</span>
                      </div>
                    </td>
                    <td>{getCountryCodes(app)}</td>
                    <td style={{textAlign: 'right'}}>
                      <div>
                        {formatNumber(getMetricValue(app, 'clicks', 'today', showStats))} <span
                        style={{color: '#cccccc', fontSize: '0.85em'}}>1d</span>
                      </div>
                      <div>
                        {formatNumber(getMetricValue(app, 'clicks', 'week', showStats))} <span
                        style={{color: '#cccccc', fontSize: '0.85em'}}>7d</span>
                      </div>
                    </td>
                    <td style={{textAlign: 'right'}}>
                      <div>
                        {formatPercentage(getMetricValue(app, 'fillRate', 'today', showStats))} <span
                        style={{color: '#cccccc', fontSize: '0.85em'}}>1d</span>
                      </div>
                      <div>
                        {formatPercentage(getMetricValue(app, 'fillRate', 'week', showStats))} <span
                        style={{color: '#cccccc', fontSize: '0.85em'}}>7d</span>
                      </div>
                    </td>
                    <td style={{textAlign: 'right'}}>
                      <div>
                        {formatNumber(getMetricValue(app, 'conversions', 'today', showStats))} <span
                        style={{color: '#cccccc', fontSize: '0.85em'}}>1d</span>
                      </div>
                      <div>
                        {formatNumber(getMetricValue(app, 'conversions', 'week', showStats))} <span
                        style={{color: '#cccccc', fontSize: '0.85em'}}>7d</span>
                      </div>
                    </td>
                    <td style={{textAlign: 'right'}}>
                      <div>
                        {formatPercentage(getMetricValue(app, 'conversionRate', 'today', showStats))} <span
                        style={{color: '#cccccc', fontSize: '0.85em'}}>1d</span>
                      </div>
                      <div>
                        {formatPercentage(getMetricValue(app, 'conversionRate', 'week', showStats))} <span
                        style={{color: '#cccccc', fontSize: '0.85em'}}>7d</span>
                      </div>
                    </td>
                    <td style={{textAlign: 'right'}}>
                      <div>
                        {formatCurrency(getMetricValue(app, 'revenue', 'today', showStats))} <span
                        style={{color: '#cccccc', fontSize: '0.85em'}}>1d</span>
                      </div>
                      <div>
                        {formatCurrency(getMetricValue(app, 'revenue', 'week', showStats))} <span
                        style={{color: '#cccccc', fontSize: '0.85em'}}>7d</span>
                      </div>
                    </td>
                    <td style={{textAlign: 'right'}}>
                      <div className="d-flex gap-2 justify-content-end">
                        <Button
                          variant="info"
                          size="sm"
                          onClick={() => onShowStats(app)}
                          title="Product Stats"
                        >
                          <i className="fas fa-chart-bar"></i>
                        </Button>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => onCreateClink(app)}
                          title="Create cLink"
                        >
                          <i className="fa fa-share-alt"></i>
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => onExpandApp(app)}
                          title="Product Details"
                          style={{ marginLeft: '4px' }}
                        >
                          <i className={app.expanded ? 'fa fa-minus' : 'fa fa-plus'}></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                  {app.expanded && (
                    <LookupExpandedRow
                      app={app}
                      onSelectTab={onSelectTab}
                      onCreateClink={onCreateClink}
                      onEditClink={onEditClink}
                      onShowTrackingLinks={onShowTrackingLinks}
                      onShowHistory={onShowHistory}
                      onShowStats={onShowStats}
                    />
                  )}
                </Fragment>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Infinite scroll trigger and loading indicator */}
      {hasMoreResults && (
        <div
          ref={observerRef}
          className="d-flex justify-content-center align-items-center"
          style={{ padding: '20px 0', minHeight: '60px' }}
        >
          {isFetching && (
            <div className="d-flex align-items-center">
              <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
              <span className="text-muted">Loading more results...</span>
            </div>
          )}
        </div>
      )}

      {/* End of results indicator */}
      {!loading && !hasMoreResults && sortedApps.length > 0 && (
        <div className="text-center py-3">
          <small className="text-muted">
            Showing all {sortedApps.length} results
          </small>
        </div>
      )}
    </>
  );
};

export default LookupResultsTable;